import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';

const navItems = [
  { icon: 'ri-dashboard-line', label: 'Dashboard', href: '/admin' },
  { icon: 'ri-bar-chart-line', label: 'Analytics', href: '/admin/analytics' },
  { icon: 'ri-edit-box-line', label: 'Content', href: '/admin/content' },
  { icon: 'ri-battery-2-charge-line', label: 'Products', href: '/admin/products' },
  { icon: 'ri-megaphone-line', label: 'Campaigns', href: '/admin/campaigns' },
  { icon: 'ri-inbox-line', label: 'Contact Inbox', href: '/admin/contact-submissions', badge: true },
  { icon: 'ri-file-list-3-line', label: 'Orders', href: '/admin/orders' },
  { icon: 'ri-store-2-line', label: 'Dealers', href: '/admin/dealers' },
  { icon: 'ri-shield-star-line', label: 'Warranty Claims', href: '/admin/warranty-claims', badge: true },
  { icon: 'ri-settings-3-line', label: 'Settings', href: '/admin/settings' },
];

interface Props {
  collapsed: boolean;
  onToggle: () => void;
}

export default function AdminSidebar({ collapsed, onToggle }: Props) {
  const location = useLocation();
  const { theme } = useTheme();
  const dark = theme === 'dark';

  return (
    <aside
      className={`flex flex-col h-screen sticky top-0 transition-all duration-300 ${
        collapsed ? 'w-16' : 'w-56'
      } ${dark ? 'bg-[#0D1B2A] border-r border-white/10' : 'bg-white border-r border-gray-200'}`}
    >
      {/* Logo */}
      <div className={`flex items-center gap-3 px-4 h-16 border-b ${dark ? 'border-white/10' : 'border-gray-200'}`}>
        {collapsed ? (
          <div className="w-8 h-8 flex items-center justify-center shrink-0">
            <i className="ri-anchor-line text-xl" style={{ color: 'var(--accent)' }}></i>
          </div>
        ) : (
          <div className="flex flex-col gap-0.5">
            <img
              src="https://static.readdy.ai/image/3bab8804b943b0f9d2e99ce4e4e8123d/a7b2a29c0c21ce8564d3744200077816.png"
              alt="Stingray"
              className={`h-7 w-auto object-contain brightness-0 ${dark ? 'invert' : ''}`}
            />
            <span
              className="font-bold tracking-[0.22em] uppercase pl-0.5"
              style={{ fontSize: '9px', color: 'var(--accent)' }}
            >
              ADMIN
            </span>
          </div>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {navItems.map((item) => {
          const active = location.pathname === item.href || (item.href !== '/admin' && location.pathname.startsWith(item.href));
          return (
            <Link
              key={item.href}
              to={item.href}
              title={collapsed ? item.label : undefined}
              className={`flex items-center gap-3 px-4 py-3 mx-2 rounded-lg mb-1 transition-all cursor-pointer whitespace-nowrap ${
                active
                  ? 'text-white shadow-sm'
                  : dark
                  ? 'text-white/60 hover:text-white hover:bg-white/5'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
              style={active ? { backgroundColor: 'var(--accent)', color: 'white' } : {}}
            >
              <div className="w-5 h-5 flex items-center justify-center shrink-0 relative">
                <i className={`${item.icon} text-base`}></i>
                {item.badge && collapsed && (
                  <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-400"></span>
                )}
              </div>
              {!collapsed && <span className="text-sm font-medium flex-1">{item.label}</span>}
              {!collapsed && item.badge && (
                <span className="text-[9px] font-black px-1.5 py-0.5 rounded-full bg-amber-400 text-white whitespace-nowrap">NEW</span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Collapse toggle */}
      <div className={`p-4 border-t ${dark ? 'border-white/10' : 'border-gray-200'}`}>
        <button
          onClick={onToggle}
          className={`w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
            dark ? 'text-white/40 hover:text-white hover:bg-white/5' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'
          }`}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className={`${collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-left-s-line'} text-lg`}></i>
          </div>
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>
    </aside>
  );
}
